import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { admin, type AdminSessionRow, type SessionClockSync } from '../../api/admin'
import { ApiError } from '../../api/client'

function QualityBadge({ quality }: { quality: string | null | undefined }) {
  if (!quality) return null
  const config: Record<string, { label: string; className: string }> = {
    complete: { label: 'Completa', className: 'tag-ok' },
    partial: { label: 'Parcial', className: 'tag-warn' },
    degraded: { label: 'Incompleta', className: 'tag-err' },
    no_data: { label: 'Sin datos', className: 'tag' },
  }
  const c = config[quality] ?? { label: quality, className: 'tag' }
  return <span className={c.className}>{c.label}</span>
}

/** Por qué la sesión no está lista, en castellano. Las claves las pone el
 *  servidor (`compute_completeness`, backend/app/services/completeness.py). */
export const MISSING_LABELS: Record<string, string> = {
  gameplay_events: 'el casco declaró eventos sin subir',
  gameplay_gap: 'hueco entre chunks de gameplay',
  gameplay_bronze: 'se acusaron eventos pero el bronce quedó vacío',
  physio_bronze: 'falta el bronce fisiológico',
  physio_samples: 'el bronce fisiológico no trajo muestras',
  // vrs-docs/36 §3.2. Es la falla que hasta 2026-09-07 sólo se veía abriendo
  // manifests a mano: el bronce está entero pero la fisiología del kit no se
  // puede poner sobre el eje del gameplay, así que los ciclos salen nulos.
  clock_unsynced: 'la fisiología del kit no se pudo alinear: relojes Quest↔Pi sin sincronizar',
  // vrs-docs/59 F1: el bronce se armó con los tramos del cierre y la subida
  // completa todavía no llegó. Se levanta sola cuando llega.
  physio_reconstructed: 'la fisiología se armó con tramos: puede faltar el tramo final',
}

const PHYSIO_SOURCE_LABELS: Record<string, string> = {
  rpi_bio: 'Pi',
  browser_polar: 'browser',
}

/** Los motivos del servidor, en una frase legible para el operador. */
export function describeMissing(missing: string[]): string {
  if (missing.length === 0) return '\u2014'
  return missing.map((m) => MISSING_LABELS[m] ?? m).join('; ')
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleString('es-AR', {
    dateStyle: 'short',
    timeStyle: 'short',
  })
}

function daysSince(value: string): number {
  return Math.floor((Date.now() - new Date(value).getTime()) / 86_400_000)
}

/** Estado del eje temporal Quest↔RPi como un chip más de la fila.
 *
 *  Existe porque la ausencia de probes era SILENCIOSA: la sesión mostraba
 *  gameplay ✔, fisio ✔, y sólo abriendo el manifest de silver se veía que el
 *  eje nunca se construyó. Acá se ve en la tabla, y `clock_unsynced` además
 *  degrada `sync_quality` (backend/app/services/completeness.py). */
export function ClockChip({ clock }: { clock: SessionClockSync | undefined }) {
  // Sin kit no hay reloj que sincronizar: la fisio del browser comparte el
  // reloj del gameplay. Mostrar un chip vacío sería ruido.
  if (!clock || clock.state === 'not_needed') return null
  const probes = clock.probes_accepted
  if (clock.state === 'unknown') {
    return (
      <span className="tag opacity-50" title="Silver todavía no corrió sobre esta sesión">
        reloj
      </span>
    )
  }
  const synced = clock.state === 'synced'
  return (
    <span
      className={synced ? 'tag-ok' : 'tag-err'}
      title={
        synced
          ? `Eje Quest↔Pi ajustado (${clock.quality ?? '—'}, ${probes ?? '—'} probes aceptadas)`
          : `Sin eje Quest↔Pi: ${probes ?? 0} probes aceptadas` +
            (clock.probes_sent_declared != null
              ? ` de ${clock.probes_sent_declared} enviadas`
              : '') +
            `. La fisiología del kit no se puede alinear con el gameplay.`
      }
    >
      {synced ? 'reloj' : 'reloj (sin sync)'}
    </span>
  )
}

/** Un artefacto de bronce/silver: presente, ausente-pero-esperado, o N/A.
 *  Distinguir los dos últimos es el punto: una sesión standalone sin fisio no
 *  es una sesión a la que le falta la fisio. */
function ArtifactChip({
  label,
  present,
  expected = true,
  title,
}: {
  label: string
  present: boolean
  expected?: boolean
  title?: string
}) {
  const className = present ? 'tag-ok' : expected ? 'tag-err' : 'tag opacity-50'
  const suffix = present ? '' : expected ? ' (falta)' : ' (no esperada)'
  return (
    <span className={className} title={`${title ?? label}${suffix}`}>
      {label}
    </span>
  )
}

export function Artifacts({ s }: { s: AdminSessionRow }) {
  const source = s.physio_source ? PHYSIO_SOURCE_LABELS[s.physio_source] ?? s.physio_source : null
  const late = s.artifacts.late_gameplay
  return (
    <div className="flex flex-wrap items-center gap-xs">
      <ArtifactChip
        label="gameplay"
        present={s.artifacts.gameplay}
        title={
          s.gameplay_events != null
            ? `Gameplay · ${s.gameplay_events} eventos${s.gameplay_gaps ? `, ${s.gameplay_gaps} huecos` : ''}`
            : 'Gameplay'
        }
      />
      <ArtifactChip
        label={source ? `fisio·${source}` : 'fisio'}
        present={s.artifacts.physio}
        expected={s.physio_expected}
        title={source ? `Fisiología escrita por ${source}` : 'Fisiología'}
      />
      <ArtifactChip
        label="silver"
        present={s.artifacts.silver}
        // Silver necesita los DOS bronces (services/silver_pipeline.py): sin
        // fisiología no se puede materializar nunca, así que marcarlo en rojo
        // sería culpar a silver de lo que le falta al bronce.
        expected={s.artifacts.gameplay && s.artifacts.physio}
        title="Silver materializado (listo para análisis)"
      />
      <ClockChip clock={s.clock_sync} />
      {late > 0 && (
        <span className="tag-info" title="Lotes de gameplay aceptados después del sellado">
          +{late} tarde
        </span>
      )}
    </div>
  )
}

export function StatusCell({ s }: { s: AdminSessionRow }) {
  if (s.status === 'incomplete_pending_recovery') {
    const since = s.pending_recovery_since
    return (
      <span
        className="tag-warn"
        title={
          since
            ? `Esperando que un dispositivo suba lo que falta desde el ${fmtDate(since)}`
            : 'Esperando una recuperación'
        }
      >
        esperando{since ? ` ${daysSince(since)}d` : ''}
      </span>
    )
  }
  return <span className={s.status === 'closed' ? 'tag-ok' : 'tag'}>{s.status}</span>
}

export function AdminSessions() {
  const [params, setParams] = useSearchParams()
  const status = params.get('status') ?? ''
  const sinceDays = params.get('since_days') ?? ''
  const incomplete = params.get('incomplete') === '1'
  const [rows, setRows] = useState<AdminSessionRow[] | null>(null)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setError(null)
    admin
      .sessions({
        status: status || undefined,
        since_days: sinceDays ? Number(sinceDays) : undefined,
        incomplete: incomplete || undefined,
      })
      .then((r) => {
        setRows(r.sessions)
        setTotal(r.total)
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Error de red.'))
  }, [status, sinceDays, incomplete])

  const patch = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next)
  }

  return (
    <section>
      <div className="page-header">
        <h1>Sesiones</h1>
        <div className="flex flex-wrap items-center gap-md">
          <p className="m-0 text-sm text-text-muted">{total} sesiones</p>
          <a href={admin.trialsCsvUrl()} download className="btn no-underline">
            Exportar trials.csv
          </a>
        </div>
      </div>
      {error && <div className="banner-err mb-md">{error}</div>}

      <div className="filter-bar">
        <label className="flex items-center gap-sm">
          Estado:
          <select
            className="input w-auto"
            value={status}
            onChange={(e) => patch('status', e.target.value)}
          >
            <option value="">todos</option>
            <option value="running">running</option>
            <option value="closed">closed</option>
            <option value="aborted">aborted</option>
            <option value="incomplete_pending_recovery">esperando recuperación</option>
          </select>
        </label>
        <label className="flex items-center gap-sm">
          Últimos:
          <select
            className="input w-auto"
            value={sinceDays}
            onChange={(e) => patch('since_days', e.target.value)}
          >
            <option value="">siempre</option>
            <option value="1">1 día</option>
            <option value="7">7 días</option>
            <option value="30">30 días</option>
          </select>
        </label>
        <label
          className="flex items-center gap-sm"
          title="Las que el servidor verificó que no están listas para análisis"
        >
          <input
            type="checkbox"
            className="checkbox"
            checked={incomplete}
            onChange={(e) => patch('incomplete', e.target.checked ? '1' : '')}
          />
          Sólo incompletas
        </label>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Cuándo</th>
              <th>Usuario</th>
              <th>Protocolo</th>
              <th>Condición</th>
              <th>Estado</th>
              <th>Calidad</th>
              <th>Artefactos</th>
              <th>Qué falta</th>
              <th className="text-right">Ver</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr>
                <td colSpan={9} className="text-text-muted">
                  Cargando…
                </td>
              </tr>
            )}
            {rows?.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-text-muted">
                  {incomplete
                    ? 'Ninguna sesión incompleta con esos filtros.'
                    : 'Sin sesiones con esos filtros.'}
                </td>
              </tr>
            )}
            {rows?.map((s) => (
              <tr key={s.id}>
                <td>{fmtDate(s.started_at)}</td>
                <td>
                  <Link to={`/admin/users/${s.user_id}`}>#{s.user_id}</Link>
                </td>
                <td>{s.protocol_id || '—'}</td>
                <td>{s.condition || '—'}</td>
                <td>
                  <StatusCell s={s} />
                </td>
                <td>
                  <QualityBadge quality={s.sync_quality} />
                </td>
                <td>
                  <Artifacts s={s} />
                </td>
                <td className="text-sm text-text-muted">{describeMissing(s.missing)}</td>
                <td className="text-right">
                  <Link to={`/admin/sessions/${s.id}`}>detalle</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
